import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Avatar,
  Divider,
  Chip,
  Stack,
  Alert,
} from "@mui/material";

// Icons
import PersonIcon from "@mui/icons-material/Person";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import EmailIcon from "@mui/icons-material/Email";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SecurityIcon from "@mui/icons-material/Security";
import EventIcon from "@mui/icons-material/Event";

import { useTheme } from "@mui/material/styles";
import { tokens } from "../theme";
import PageContainer from "../layouts/PageContainer";
import AppButton from "../components/buttons/AppButton";
import StatusChip from "../components/common/StatusChip";
import AppCircularProgress from "../components/common/AppCircularProgress";

// Redux Actions
import { getUserById } from "../redux/actions/userActions";

const ProfilePage = React.memo(() => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const dispatch = useDispatch();

  // --- Redux State ---
  const { userInfo, user, loadingUser, errorLoadingUser } = useSelector(
    (state) => state.user,
  );

  // Fetch full user details on mount
  useEffect(() => {
    if (userInfo?.id) {
      dispatch(getUserById(userInfo.id));
    }
  }, [dispatch, userInfo]);

  // --- Helper to Group Permissions ---
  const groupedPermissions = useMemo(() => {
    if (!user?.permissions) return {};
    return user.permissions.reduce((acc, permission) => {
      const [group] = permission.split(":");
      if (!acc[group]) acc[group] = [];
      acc[group].push(permission);
      return acc;
    }, {});
  }, [user]);

  const renderContent = () => {
    if (loadingUser) {
      return (
        <Box display="flex" justifyContent="center" my={10}>
          <AppCircularProgress />
        </Box>
      );
    }

    if (errorLoadingUser) {
      return (
        <Box textAlign="center" py={10}>
          <Alert severity="error" sx={{ display: "inline-flex" }}>
            {errorLoadingUser}
          </Alert>
          <Box mt={2}>
            <AppButton onClick={() => dispatch(getUserById(userInfo.id))}>
              Retry
            </AppButton>
          </Box>
        </Box>
      );
    }

    if (!user) return null;

    const fullName = `${user.firstName} ${user.lastName}`;

    return (
      <Grid container spacing={3}>
        {/* LEFT COLUMN: Identity */}
        <Grid item xs={12} md={4}>
          <Stack spacing={3}>
            <Card
              sx={{ borderRadius: 2, border: `1px solid ${colors.grey[700]}` }}
            >
              <CardContent sx={{ p: 4, textAlign: "center" }}>
                <Avatar
                  sx={{
                    bgcolor: colors.gradients.brandPrimary,
                    width: 100,
                    height: 100,
                    mx: "auto",
                    mb: 2,
                    boxShadow: 3,
                  }}
                >
                  <PersonIcon sx={{ fontSize: 50, color: "#fff" }} />
                </Avatar>

                <Typography variant="h5" fontWeight="700" gutterBottom>
                  {fullName}
                </Typography>

                <Chip
                  label={user.userRole?.replace(/_/g, " ")}
                  color="primary"
                  icon={<AdminPanelSettingsIcon />}
                  sx={{ mb: 2, fontWeight: "bold" }}
                />

                <Divider sx={{ my: 2 }} />

                <Stack spacing={2} textAlign="left">
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <EmailIcon color="action" fontSize="small" />
                    <Typography variant="body2">{user.email}</Typography>
                  </Stack>
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <CheckCircleIcon color="action" fontSize="small" />
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="body2">Status:</Typography>
                      <StatusChip status={user.userStatus} />
                    </Box>
                  </Stack>
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <EventIcon color="action" fontSize="small" />
                    <Typography variant="body2">
                      Joined: {new Date(user.createdAt).toLocaleDateString()}
                    </Typography>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>

            <Box
              p={2}
              bgcolor={colors.grey[800]}
              borderRadius={1}
              border={`1px solid ${colors.grey[700]}`}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                textAlign="center"
              >
                Profile details and permissions are managed by the System
                Administrator.
              </Typography>
            </Box>
          </Stack>
        </Grid>

        {/* RIGHT COLUMN: Permissions Grid */}
        <Grid item xs={12} md={8}>
          <Card
            sx={{
              height: "100%",
              borderRadius: 2,
              border: `1px solid ${colors.grey[700]}`,
            }}
          >
            <CardContent sx={{ p: 4 }}>
              <Stack direction="row" alignItems="center" spacing={2} mb={3}>
                <Avatar
                  sx={{
                    background: colors.gradients.success,
                    width: 50,
                    height: 50,
                  }}
                >
                  <SecurityIcon sx={{ fontSize: 26, color: "#fff" }} />
                </Avatar>
                <Box>
                  <Typography variant="h5" fontWeight="700">
                    Effective Permissions
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Access rights granted to your role (
                    {user.permissions?.length || 0} total)
                  </Typography>
                </Box>
              </Stack>
              <Divider sx={{ mb: 3 }} />

              {Object.keys(groupedPermissions).length > 0 ? (
                <Grid container spacing={3}>
                  {Object.entries(groupedPermissions)
                    .sort()
                    .map(([groupName, perms]) => (
                      <Grid item xs={12} sm={6} md={4} key={groupName}>
                        <Box
                          p={2}
                          borderRadius={1}
                          border={`1px solid ${colors.grey[800]}`}
                          bgcolor={
                            theme.palette.mode === "dark"
                              ? "rgba(0,0,0,0.2)"
                              : "rgba(0,0,0,0.02)"
                          }
                          height="100%"
                        >
                          <Typography
                            variant="subtitle2"
                            fontWeight="bold"
                            sx={{ textTransform: "capitalize", mb: 1.5 }}
                          >
                            {groupName.replace(/_/g, " ")}
                          </Typography>
                          <Stack spacing={1}>
                            {perms.sort().map((p) => (
                              <Chip
                                key={p}
                                label={p.split(":")[1] || p}
                                size="small"
                                variant="outlined"
                                sx={{
                                  justifyContent: "flex-start",
                                  borderRadius: 1,
                                }}
                              />
                            ))}
                          </Stack>
                        </Box>
                      </Grid>
                    ))}
                </Grid>
              ) : (
                <Box textAlign="center" py={5}>
                  <Typography color="text.secondary">
                    No specific permissions assigned to this account.
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  return (
    <PageContainer
      title="My Profile"
      subtitle="View your personal details and system access rights."
      breadcrumbs={[
        { label: "Dashboard", to: "/dashboard" },
        { label: "Profile", to: null },
      ]}
    >
      {renderContent()}
    </PageContainer>
  );
});

export default ProfilePage;
